import React, { FC } from 'react'
import { WorkspaceNameText, HelpText } from './ParentAccessLevelDropdown.styles'
import { ShareForSettingsModal } from '../types'
import {
  ParentPermissionsAccessSelect,
  TriggerButton,
  ParentAccessLevelType,
} from 'components/AccessLevelSelect'
import { Truncate, Tooltip, WorkspaceLogo } from '@sketch/components'
import PermissionWrapper from '../PermissionWrapper'

interface ParentAccessLevelDropdownProps {
  share: Pick<
    ShareForSettingsModal,
    'allowedParentAccessLevels' | 'isDraft' | 'workspace'
  >
  level: ParentAccessLevelType
  setLevel: (value: ParentAccessLevelType) => void
  workspaceMembersCount?: number
}

const getHelpText = (workspaceMembersCount: number | undefined) => {
  if (workspaceMembersCount === undefined) {
    return `Loading…`
  }

  if (workspaceMembersCount === 1) {
    return `${workspaceMembersCount} Workspace Member`
  }

  return `${workspaceMembersCount} Workspace Members`
}

const ParentAccessLevelDropdown: FC<ParentAccessLevelDropdownProps> = ({
  share,
  level,
  setLevel,
  workspaceMembersCount,
}) => {
  const helpText = share.isDraft
    ? 'Cannot access the document.'
    : getHelpText(workspaceMembersCount)

  // We should already have a workspace once we reach this place
  const workspace = share.workspace!
  const access = {
    kind: 'workspace' as const,
    level,
    setLevel,
  }
  return (
    <PermissionWrapper data-testid="workspace-access-level-dropdown">
      <WorkspaceLogo
        src={workspace.avatar?.small}
        workspaceName={workspace.name}
        size="32px"
      />
      <Truncate>
        <WorkspaceNameText>{workspace.name}</WorkspaceNameText>
        <HelpText>{helpText}</HelpText>
      </Truncate>
      {share.isDraft ? (
        <Tooltip
          placement="top-end"
          spacing="0"
          content="You can share documents in My Drafts with specific people, but not the whole Workspace"
        >
          <TriggerButton disabled label="No Access" />
        </Tooltip>
      ) : (
        <ParentPermissionsAccessSelect
          level={(access.level as string) === 'INSPECT' ? 'VIEW' : access.level}
          setLevel={access.setLevel}
        />
      )}
    </PermissionWrapper>
  )
}

export { ParentAccessLevelDropdown }
